import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Checkbox,
  List,
  ThemeIcon,
  Flex,
  Space,
  Timeline,
} from "@mantine/core";
import {
  IconCheck,
  IconClipboardList,
  IconBrandGoogle,
  IconMessageChatbot,
  IconShieldCheck,
  IconClock,
} from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ConsentLanding() {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  // Clickworker params
  const clickworkerUser = query.get("user") || "";
  const clickworkerUserId = query.get("user_id") || "";
  const clickworkerTaskId = query.get("task_id") || "";
  const clickworkerJobId = query.get("job_id") || "";

  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const backendApiUrl = process.env.REACT_APP_API_URL_2 || "https://api.dbackup.cloud";

  // Track landing page visit
  const trackStep = useCallback(async (stepName, status = "completed", metadata = {}) => {
    try {
      if (!clickworkerUserId) return;

      await axios.post(`${backendApiUrl}/track/step`, {
        user_id: clickworkerUserId,
        step_name: stepName,
        donation_type: "both",
        status: status,
        metadata: {
          clickworker_user: clickworkerUser,
          clickworker_user_id: clickworkerUserId,
          clickworker_task_id: clickworkerTaskId,
          clickworker_job_id: clickworkerJobId,
          ...metadata,
        },
      });
      console.log(`Step tracked: ${stepName}`);
    } catch (err) {
      console.error("Failed to track step:", err);
    }
  }, [clickworkerUserId, backendApiUrl, clickworkerUser, clickworkerTaskId, clickworkerJobId]);

  // Track page visit on load
  useEffect(() => {
    if (clickworkerUserId) {
      trackStep("consent_landing_visited", "completed");
    }
  }, [clickworkerUserId, trackStep]);

  const handleProceed = async () => {
    if (!consentChecked) return;

    setIsSubmitting(true);

    // Track consent given
    await trackStep("consent_given", "completed");

    // Preserve all query params and redirect to create-uuid
    const currentQueryString = location.search;
    navigate(`/create-uuid${currentQueryString}`);
  };

  return (
    <Container size="md" py="xl">
      <Title order={1} align="center" mb="md">
        Data Donation Study
      </Title>

      <Text align="center" color="dimmed" mb="xl">
        Thank you for participating in our research study. Please read the information below carefully before proceeding.
      </Text>


      {/* Process Overview */}
      <Card withBorder shadow="sm" mb="lg" p="lg">
        <Title order={3} mb="md">
          <Flex align="center" gap="xs">
            <IconClock size={24} />
            What You'll Do
          </Flex>
        </Title>

        <Timeline active={-1} bulletSize={32} lineWidth={2}>
          <Timeline.Item
            bullet={<IconClipboardList size={16} />}
            title={<Text weight={600}>Step 1: Complete Survey</Text>}
          >
            <Text color="dimmed" size="sm">
              Answer a brief questionnaire about your demographics and preferences.
            </Text>
          </Timeline.Item>

          <Timeline.Item
            bullet={<IconBrandGoogle size={16} />}
            title={<Text weight={600}>Step 2: Google Takeout Export</Text>}
          >
            <Text color="dimmed" size="sm">
              Export your Google activity data using Google Takeout and share it with our secure service.
            </Text>
          </Timeline.Item>

          <Timeline.Item
            bullet={<IconMessageChatbot size={16} />}
            title={<Text weight={600}>Step 3: ChatGPT Data Export</Text>}
          >
            <Text color="dimmed" size="sm">
              Export your ChatGPT conversation data and upload it to our platform.
            </Text>
          </Timeline.Item>

          <Timeline.Item
            bullet={<IconCheck size={16} />}
            title={<Text weight={600}>Step 4: Receive Success Code</Text>}
          >
            <Text color="dimmed" size="sm">
              After completing all steps, you'll receive a success code to submit for your reward.
            </Text>
          </Timeline.Item>
        </Timeline>
      </Card>

      {/* What We Collect */}
      <Card withBorder shadow="sm" mb="lg" p="lg">
        <Title order={3} mb="md">
          What Data We Collect
        </Title>

        <List
          spacing="sm"
          icon={
            <ThemeIcon color="blue" size={20} radius="xl">
              <IconCheck size={12} />
            </ThemeIcon>
          }
        >
          <List.Item>
            <Text weight={500}>Survey Responses</Text>
            <Text size="sm" color="dimmed">Your answers to demographic and preference questions</Text>
          </List.Item>
          <List.Item>
            <Text weight={500}>Google Activity Data</Text>
            <Text size="sm" color="dimmed">Your Google search and browsing activity from Google Takeout</Text>
          </List.Item>
          <List.Item>
            <Text weight={500}>ChatGPT Conversations</Text>
            <Text size="sm" color="dimmed">Text-based conversation history (images, videos, and audio are automatically excluded)</Text>
          </List.Item>
        </List>
      </Card>

      {/* Privacy & Security */}
      <Card withBorder shadow="sm" mb="lg" p="lg">
        <Title order={3} mb="md">
          <Flex align="center" gap="xs">
            <IconShieldCheck size={24} />
            Privacy & Security
          </Flex>
        </Title>

        <List
          spacing="sm"
          icon={
            <ThemeIcon color="green" size={20} radius="xl">
              <IconCheck size={12} />
            </ThemeIcon>
          }
        >
          <List.Item>
            <Text weight={500}>Data Anonymization</Text>
            <Text size="sm" color="dimmed">All personal identifiers are removed before analysis</Text>
          </List.Item>
          <List.Item>
            <Text weight={500}>Secure Storage</Text>
            <Text size="sm" color="dimmed">Your data is encrypted and stored securely</Text>
          </List.Item>
          <List.Item>
            <Text weight={500}>Research Purposes Only</Text>
            <Text size="sm" color="dimmed">Data is used solely for academic research and will never be sold</Text>
          </List.Item>
          <List.Item>
            <Text weight={500}>Right to Withdraw</Text>
            <Text size="sm" color="dimmed">You can request data deletion at any time by contacting us</Text>
          </List.Item>
        </List>

        <Space h="md" />

        <Text size="sm" color="dimmed">
          For more details, please review our{" "}
          <Text component="a" href="/privacypolicy" color="blue" target="_blank">
            Privacy Policy
          </Text>{" "}
          and{" "}
          <Text component="a" href="/terms" color="blue" target="_blank">
            Terms & Conditions
          </Text>
          .
        </Text>
      </Card>

      {/* Consent Checkbox */}
      <Card withBorder shadow="sm" mb="lg" p="lg" style={{ backgroundColor: "#f8f9fa" }}>
        <Checkbox
          label={
            <Text>
              I have read and understood the information above. I consent to participate in this study and agree to share my data as described. I understand that my participation is voluntary and I can withdraw at any time.
            </Text>
          }
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.currentTarget.checked)}
          size="md"
        />
      </Card>

      {/* Proceed Button */}
      <Button
        fullWidth
        size="lg"
        onClick={handleProceed}
        disabled={!consentChecked}
        loading={isSubmitting}
      >
        I Agree - Start the Study
      </Button>

      <Text align="center" size="xs" color="dimmed" mt="md">
        By clicking the button above, you confirm your consent to participate.
      </Text>
    </Container>
  );
}
